import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Chips, FormControl, FormField} from 'platform/components';
import {Box, Grid, Heading, Show, Space, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {Nullish} from 'utility-types';

import {useEffect} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isEmpty, isNil, isNotEmpty, isNotNil, sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {ELECTRIC_TYPES} from '../../../constants/electricTypes';
import {useCatalogue} from '../../../hooks/useCatalogue';
import {usePriceReport} from '../../../hooks/usePriceReport';
import i18n from '../../../i18n/i18n';
import {useGetApiCountriesQuery, useGetTrimLevelsQuery} from '../../../store/catalogueApi';
import {featureFlags} from '../../../utils/featureFlags';
import {getYearOptions} from '../../../utils/getYearOptions';
import {DRIVE_4X4} from '../constants/drive4x4';
import {EXCLUDED_FUEL_TYPES} from '../constants/excludedFuelTypes';
import {MAX_SEAT_COUNT} from '../constants/maxSeatCount';
import {WITHOUT_VAT} from '../constants/withoutVAT';
import {WITH_VAT} from '../constants/withVAT';
import {
  useFilterRelevantOptions,
  useGetRelevantPowerOptions,
} from '../hooks/useFilterRelevantOptions';
import {VehicleFormType} from '../types/VehicleFormType';
import {MakeModelFields} from './MakeModelFields';
import {VinParser} from './VinParser';

interface VehicleFieldsProps extends TestIdProps {
  control: FormControl<VehicleFormType>;
  formApi: UseFormReturn<VehicleFormType>;
}

const seatCountOptions = Array.from(Array(MAX_SEAT_COUNT)).map((_, index) => ({
  value: `${index + 1}`,
  label: `${index + 1}`,
}));

export function VehicleFields(props: VehicleFieldsProps) {
  const {
    fuelOptions: allFuelOptions,
    styleOptions,
    transmissionOptions,
    driveOptions,
    colorOptions,
    doorOptions,
    interiorMaterialOptions,
  } = useCatalogue();
  const {requestData} = usePriceReport();

  const allowedFuelOptions = allFuelOptions?.filter(
    (option) => !EXCLUDED_FUEL_TYPES.includes(option.value)
  );
  const firstRegistrationYearOptions = getYearOptions();

  const make = props.formApi.watch('make');
  const modelFamily = props.formApi.watch('modelFamily');
  const firstRegistrationOnYear = props.formApi.watch('firstRegistrationOnYear');
  const transmission = props.formApi.watch('transmission')?.[0];
  const fuelType = props.formApi.watch('fuelType')?.[0];
  const bodyStyle = props.formApi.watch('bodyStyle')?.[0];
  const drive = props.formApi.watch('drive')?.[0];
  const power = props.formApi.watch('power');

  const {data: apiCountries, isLoading: isApiCountriesLoading} = useGetApiCountriesQuery({
    format: 'iso3',
  });
  const {currentData: trimLevels, isFetching: isTrimLevelsLoading} = useGetTrimLevelsQuery(
    isNil(make) || isNil(modelFamily)
      ? skipToken
      : {
          modelFamily,
          make,
          country: isNotNil(requestData?.market)
            ? getSafely(apiCountries, requestData.market)
            : undefined,
          yearFrom: firstRegistrationOnYear ?? undefined,
          yearTo: firstRegistrationOnYear ?? undefined,
        }
  );

  const hasTrimLevels =
    !isApiCountriesLoading && !isTrimLevelsLoading && isNotNilOrEmpty(trimLevels?.trim_levels);
  const trimLevelsOptions = sort(
    (a, b) => {
      if (isNotNil(a.label) && isNotNil(b.label)) {
        return a.label.localeCompare(b.label);
      }
      return 0;
    },
    trimLevels?.trim_levels?.map((trimLevel) => ({
      label: trimLevel.label,
      value: trimLevel.const_key,
    })) ?? []
  );

  useEffect(() => {
    if (
      isNotNil(trimLevels) &&
      !trimLevels.trim_levels
        ?.map((trimLevel) => trimLevel.const_key)
        .includes(props.formApi.getValues('trimLevel'))
    ) {
      props.formApi.setValue('trimLevel', '');
    }
  }, [trimLevels, props.formApi]);

  const shouldShowMaxPowerInput =
    isNotNil(fuelType) &&
    ELECTRIC_TYPES.includes(fuelType) &&
    isFeatureEnabled(featureFlags.PR_SPLIT_POWER);

  const filters = {
    make,
    model: modelFamily,
    year: firstRegistrationOnYear?.toString(),
    transmission,
    fuel_type: fuelType,
    car_style: bodyStyle,
    drive,
    power: power?.toString(),
  };
  const {options: transmissionOptionsFiltered, limit: transmissionOptionsLimit} =
    useFilterRelevantOptions(transmissionOptions ?? [], 'transmission', filters);
  const {options: fuelTypeOptionsFiltered, limit: fuelTypeOptionsLimit} = useFilterRelevantOptions(
    allowedFuelOptions ?? [],
    'fuel_type',
    filters
  );
  const {options: bodyStyleOptionsFiltered, limit: bodyStyleOptionsLimit} =
    useFilterRelevantOptions(styleOptions ?? [], 'car_style', filters);
  const {options: driveOptionsFiltered, limit: driveOptionsLimit} = useFilterRelevantOptions(
    driveOptions ?? [],
    'drive',
    filters
  );
  const is4x4Options = driveOptionsFiltered.map((option) => ({
    label:
      option.value === DRIVE_4X4
        ? i18n.t('page.create.labels.yes')
        : i18n.t('page.create.labels.no'),
    value: option.value,
  }));
  const powerOptions = useGetRelevantPowerOptions(filters);
  const powerOptionsWithOther = [
    ...powerOptions,
    {value: '', label: i18n.t('page.create.labels.other')},
  ];
  const isPowerInOptions = powerOptions.some((option) => option.value === power?.toString());
  const shouldShowPowerInput = isEmpty(powerOptions) || (!isPowerInOptions && power !== undefined);
  const powerChipValue = match([power, isPowerInOptions])
    .with([Pattern.number, true], () => [power?.toString() ?? ''])
    .with([Pattern.number, false], () => [''])
    .with([null, Pattern.any], () => [''])
    .otherwise(() => []);
  const onPowerChipChange = (value: string[] | Nullish) => {
    match(value)
      .with([''], () => {
        props.formApi.setValue('power', null, {shouldTouch: true});
      })
      .with([Pattern.string], ([value]) => {
        props.formApi.setValue('power', Number(value), {shouldTouch: true});
      })
      .otherwise(() => {
        props.formApi.setValue('power', undefined, {shouldTouch: true});
      });
    props.control.onChange();
  };

  return (
    <VStack spacing={6}>
      <Heading size={2}>{i18n.t('page.create.labels.vehicleSpecification')}</Heading>
      <VinParser
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('vinParser', props)}
      />
      <MakeModelFields
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('makeModelFields', props)}
      />
      <Grid columns={[1, 1, 2]} spacing={6}>
        <FormField
          control={props.control}
          type="choice"
          name="firstRegistrationOnYear"
          label={i18n.t('page.create.labels.firstRegistrationOnYear')}
          isRequired
          options={firstRegistrationYearOptions}
          data-testid={suffixTestId('form-firstRegistrationOnYear', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="trimLevel"
          label={i18n.t('page.create.labels.trimLevel')}
          options={trimLevelsOptions}
          isDisabled={!hasTrimLevels}
          data-testid={suffixTestId('form-trimLevel', props)}
        />
      </Grid>
      <FormField
        control={props.control}
        type="chips"
        name="transmission"
        label={i18n.t('page.create.labels.transmission')}
        isRequired
        isDeselectable
        options={transmissionOptionsFiltered}
        limit={transmissionOptionsLimit}
        data-testid={suffixTestId('form-transmission', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="fuelType"
        label={i18n.t('page.create.labels.fuelType')}
        isRequired
        isDeselectable
        options={fuelTypeOptionsFiltered}
        limit={fuelTypeOptionsLimit}
        data-testid={suffixTestId('form-fuelType', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="bodyStyle"
        label={i18n.t('page.create.labels.bodyStyle')}
        isRequired
        isDeselectable
        options={bodyStyleOptionsFiltered}
        limit={bodyStyleOptionsLimit}
        data-testid={suffixTestId('form-bodyStyle', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="drive"
        label={i18n.t('page.create.labels.drive4x4')}
        isRequired
        isDeselectable
        options={is4x4Options}
        limit={driveOptionsLimit}
        data-testid={suffixTestId('form-drive', props)}
      />
      <Show when={isNotEmpty(powerOptions)}>
        <Chips
          name="power"
          value={powerChipValue}
          onChange={onPowerChipChange}
          label={i18n.t('page.create.labels.power')}
          options={powerOptionsWithOther}
          isRequired
          isDeselectable
          data-testid={suffixTestId('form-power', props)}
        />
      </Show>
      <Show when={shouldShowPowerInput || shouldShowMaxPowerInput}>
        <Grid columns={[1, 1, 2]} spacing={6}>
          <Show when={shouldShowPowerInput}>
            <FormField
              control={props.control}
              type="number"
              name="power"
              label={`${i18n.t('page.create.labels.power')} (${i18n.t('general.metric.kW')})`}
              tooltip={
                isFeatureEnabled(featureFlags.PR_SPLIT_POWER)
                  ? i18n.t('page.create.labels.powerTooltip')
                  : undefined
              }
              isRequired
              data-testid={suffixTestId('form-power', props)}
            />
          </Show>
          <Show when={shouldShowMaxPowerInput}>
            <FormField
              control={props.control}
              type="number"
              name="maxPower"
              label={`${i18n.t('page.create.labels.maxPower')} (${i18n.t('general.metric.kW')})`}
              tooltip={i18n.t('page.create.labels.maxPowerTooltip')}
              data-testid={suffixTestId('form-maxPower', props)}
            />
          </Show>
        </Grid>
      </Show>
      <Grid columns={[1, 2]} spacing={6}>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="number"
            name="mileage"
            label={`${i18n.t('page.create.labels.mileage')} (${i18n.t('general.metric.km')})`}
            isRequired
            data-testid={suffixTestId('form-mileage', props)}
          />
        </Box>
        <Show onDesktop onNotebook>
          <Space fillAvailable />
        </Show>
      </Grid>
      <FormField
        control={props.control}
        type="chips"
        name="exteriorColor"
        label={i18n.t('page.create.labels.exteriorColor')}
        options={colorOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-exteriorColor', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="interiorMaterial"
        label={i18n.t('page.create.labels.interiorMaterial')}
        options={interiorMaterialOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-interiorMaterial', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="isVatDeductible"
        label={i18n.t('page.create.labels.vatDeductible')}
        options={[
          {label: i18n.t('page.create.labels.yes'), value: WITH_VAT},
          {label: i18n.t('page.create.labels.no'), value: WITHOUT_VAT},
        ]}
        isDeselectable
        data-testid={suffixTestId('form-isVatDeductible', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="doorCount"
        label={i18n.t('page.create.labels.doorCount')}
        options={doorOptions ?? []}
        isDeselectable
        data-testid={suffixTestId('form-doorCount', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="seatCount"
        label={i18n.t('page.create.labels.seatCount')}
        options={seatCountOptions}
        isDeselectable
        data-testid={suffixTestId('form-seatCount', props)}
      />
    </VStack>
  );
}
